@switch (type) {
  @case ('list-item') {
    <div class="list-item">
      <div class="details">
        <div class="icon-name">
          <rt-icon
            name="attach-file"
            margin-right-small-xx
            colorToken="colorIconLowDefault"
          ></rt-icon>
          @if (isDocumentOrImage) {
            <a class="link" (click)="open()">{{ attachment.filename }}</a>
          }
          @if (!isDocumentOrImage) {
            <a class="link" [href]="attachment.fileUrl" (click)="onAnchorClick($event)">{{
              attachment.filename
            }}</a>
          }
          @if (attachment.fileSize) {
            <span text-light class="size">({{ attachment.fileSize | rtFileSize }})</span>
          }
        </div>
        @if (hasDeleteAction) {
          <rt-icon-button
            variant="plain"
            (click)="delete()"
            size="small"
            class="danger-on-hover"
            shape="square"
            iconName="delete"
          >
          </rt-icon-button>
        }
      </div>
      <rt-divider variant="line dark" margin-top-small> </rt-divider>
    </div>
  }

  @case ('card') {
    <div
      class="rt-card"
      [class.additional-opts-visible]="additionalOptsDropdownOpen"
      [class.private]="isPrivate"
    >
      <ng-container *ngTemplateOutlet="preview"></ng-container>
      <ng-container *ngTemplateOutlet="footer"></ng-container>
    </div>
  }

  @case ('compact-card') {
    <div
      class="rt-card rt-card-background-light attachment-small"
      [class.additional-opts-visible]="additionalOptsDropdownOpen"
      [class.private]="isPrivate"
    >
      <ng-container *ngTemplateOutlet="preview"></ng-container>
      <ng-container *ngTemplateOutlet="footer"></ng-container>
    </div>
  }

  @case ('tile') {
    <div
      class="rt-card rt-card-background-light attachment-small-x"
      [class.additional-opts-visible]="additionalOptsDropdownOpen"
      [class.private]="isPrivate"
    >
      <ng-container *ngTemplateOutlet="footer"></ng-container>
    </div>
  }

  @case ('preview') {
    <div
      class="rt-card rt-card-background-light attachment-small"
      [class.additional-opts-visible]="additionalOptsDropdownOpen"
      [class.private]="isPrivate"
      [class.yellow]="yellowBorder"
    >
      <ng-container *ngTemplateOutlet="preview"></ng-container>
    </div>
  }
  @case ('preview-large') {
    <div
      class="rt-card attachment-large"
      [class.additional-opts-visible]="additionalOptsDropdownOpen"
      [class.private]="isPrivate"
      [class.yellow]="yellowBorder"
    >
      <ng-container *ngTemplateOutlet="preview"></ng-container>
    </div>
  }
}

<ng-template #preview>
  <div class="preview">
    @if (imageLoading) {
      <rt-spinner></rt-spinner>
    }
    @if (!imageLoading) {
      @if (hasThumbnail) {
        <img [src]="thumbnailUrl" (error)="thumbnailLoadError()" />
      }
      @if (!hasThumbnail && emptyStateTemplate) {
        <ng-container [ngTemplateOutlet]="emptyStateTemplate"> </ng-container>
      }
      @if (!hasThumbnail && !emptyStateTemplate) {
        <div class="extension">
          <div class="extension__oval">
            <div text-uppercase text-weight-semi-bold class="extension__name">
              {{ attachment.extension }}
            </div>
          </div>
        </div>
      }
    }
    <div class="overlay">
      <div class="overlay__inner">
        @if (actions.length) {
          <div class="actions">
            @if (actions.length === 1) {
              <rt-icon-button
                [ngClass]="actions[0].variant || {}"
                [rtTooltip]="actions[0].label | translate"
                (click)="actions[0].handler()"
                variant="plain-onoverlay"
                shape="square"
                [size]="type === 'compact-card' ? 'small' : 'medium'"
                [iconName]="actions[0].icon"
                class="delete-button"
              >
              </rt-icon-button>
            } @else {
              <rt-icon-button
                size="small"
                shape="square"
                iconName="more-horiz"
                variant="plain-onoverlay"
                #additionalOptsTrigger="rtOverlayOrigin"
                rt-overlay-origin
                (click)="toggleAdditionalOptsDropdown(true, $event)"
                [pressed]="additionalOptsDropdownOpen"
                data-cy="candidate-profile:candidate-profile-attachment:more-button"
              >
              </rt-icon-button>

              <ng-template
                #additionalOpts="rtConnectedOverlay"
                rt-connected-overlay
                [hasBackdrop]="true"
                [offsetX]="-5"
                [origin]="additionalOptsTrigger"
                [open]="additionalOptsDropdownOpen"
                (backdropClick)="toggleAdditionalOptsDropdown(false)"
              >
                <rt-dropdown (click)="$event.preventDefault()">
                  @for (action of actions; track trackByLabel($index, action)) {
                    <rt-dropdown-item (click)="action.handler()">
                      <rt-icon prefix [name]="action.icon"></rt-icon>
                      {{ action.label | translate }}
                    </rt-dropdown-item>
                  }
                </rt-dropdown>
              </ng-template>
            }
          </div>
        }

        @if (isDocumentOrImage) {
          <div (click)="open()" class="open-preview-layer">
            <rt-icon name="visibility" size="medium" colorToken="colorIconOnoverlayDefault">
            </rt-icon>
          </div>
        }

        @if (!isDocumentOrImage && hasDownloadAction) {
          <a
            rt-button
            [class.small]="type === 'compact-card'"
            [href]="href"
            download
            (click)="onAnchorClick($event)"
            variant="primary"
            class="download-btn"
          >
            <rt-icon name="file-download" size="medium"> </rt-icon>
            <span>{{ translations.previewDialog.downloadFile | translate }}</span>
          </a>
        }
      </div>
    </div>
  </div>
</ng-template>

<ng-template #footer>
  <div class="footer">
    @if (!hasDownloadAction) {
      <span text-bold>{{ attachment.filename }}</span>
    }
    @if (hasDownloadAction) {
      <div class="details">
        @if (type === 'tile') {
          <rt-icon name="attach-file"> </rt-icon>
        }
        @if (isPrivate) {
          <rt-icon
            [rtTooltip]="'rtu.attachment.private_file_tooltip' | translate"
            [rtTooltipMaxWidth]="200"
            name="lock"
          >
          </rt-icon>
        }
        <rt-truncate>
          @if (attachment.filename) {
            @if (!highlightedFilename && isDocumentOrImage) {
              <div class="name" (click)="open()">
                {{ attachment.filename }}
              </div>
            }
            @if (highlightedFilename && isDocumentOrImage) {
              <div
                [innerHtml]="highlightedFilename"
                (click)="open()"
                class="name name--highlighted"
              ></div>
            }
            @if (!highlightedFilename && !isDocumentOrImage) {
              <a
                href="{{ attachment.fileUrl }}"
                class="name"
                (click)="onAnchorClick($event)"
                download
              >
                {{ attachment.filename }}
              </a>
            }
            @if (highlightedFilename && !isDocumentOrImage) {
              <a
                href="{{ attachment.fileUrl }}"
                [innerHtml]="highlightedFilename"
                class="name name--highlighted"
                (click)="onAnchorClick($event)"
                download
              >
              </a>
            }
          }
        </rt-truncate>

        @if (footerIcon || moreTemplate) {
          <div layout-horizontal>
            @if (footerIcon) {
              <div class="footer__icon-wrap">
                <rt-icon [name]="footerIcon"></rt-icon>
              </div>
            }
            @if (moreTemplate) {
              <div>
                <rt-icon-button
                  size="small"
                  iconName="more-horiz"
                  shape="square"
                  #moreDropdownOrigin="rtOverlayOrigin"
                  [pressed]="moreDropdown.open"
                  rt-overlay-origin
                  variant="plain"
                  (click)="moreDropdown.open = !moreDropdown.open"
                >
                </rt-icon-button>

                <ng-template
                  #moreDropdown="rtConnectedOverlay"
                  rt-connected-overlay
                  [hasBackdrop]="true"
                  [origin]="moreDropdownOrigin"
                  (backdropClick)="moreDropdown.open = false"
                >
                  <ng-template
                    [ngTemplateOutlet]="moreTemplate"
                    [ngTemplateOutletContext]="{ attachmentAssignment: attachmentAssignment }"
                  >
                  </ng-template>
                </ng-template>
              </div>
            }
          </div>
        }
      </div>
    }
    @if (hasUploader && type === 'card') {
      <div class="user">
        <rt-avatar
          [type]="avatarData.type"
          [size]="'small'"
          [shape]="'oval'"
          [imageSrc]="avatarData.photoThumbUrl"
          [initials]="avatarData.initials"
          [context]="avatarData.context"
          [iconName]="avatarData.iconName!"
          [customIconBackgroundColor]="avatarData.customIconBackgroundColor"
          [customIconColor]="avatarData.customIconColor"
        ></rt-avatar>
        <div layout-vertical>
          <div layout>
            <div class="user-name" text-weight-semi-bold>{{ uploaderName }}</div>
            @if (attachment.guestId) {
              <rt-badge
                color="optional"
                [text]="'app.common.guest' | translate"
                margin-left-small-x
              >
              </rt-badge>
            }
          </div>
          <rt-date [time]="attachment.createdAt"> </rt-date>
        </div>
      </div>
    }
  </div>
</ng-template>
